import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Plus, Trash2, Receipt } from 'lucide-react';

// Editor for rule #6, the escalating overdue sequence. Each step is sent once
// per invoice, on the day the invoice reaches it; an invoice that is already
// further overdue gets only its latest step. Paid, void and draft invoices are
// never chased, and nothing due before the start date is touched.
const VARS = ['{customer}', '{company}', '{number}', '{amount}', '{days}', '{dueDate}', '{payUrl}', '{phone}'];

export default function OverdueSequenceCard({ cfg, upd, onSave, saving }) {
  const steps = [...(cfg.overdueSteps || [])].sort((a, b) => Number(a.days) - Number(b.days));
  const setStep = (i, patch) => upd('overdueSteps')(steps.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  return (
    <Card className="p-5 border-slate-200 space-y-4" data-testid="overdue-sequence-card">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#0d2818]/10 flex items-center justify-center flex-shrink-0"><Receipt className="w-4 h-4 text-[#0d2818]" /></div>
        <div className="flex-1">
          <div className="font-bold text-slate-900">Overdue reminder sequence</div>
          <p className="text-sm text-slate-500">Turn it on under Scheduled rules (“Overdue invoice reminder sequence”). Each step goes out once, on the day an invoice reaches it, by text and/or email. It stops as soon as the invoice is paid.</p>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="text-xs text-slate-500 mb-1">Only chase invoices due on or after</div>
          <Input type="date" value={(cfg.overdueStartDate || '').slice(0, 10)} onChange={(e) => upd('overdueStartDate')(e.target.value)} className="h-9 w-44" data-testid="overdue-start-date" />
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1">Most reminders per day</div>
          <Input type="number" min="1" value={cfg.overdueMaxPerRun ?? 50} onChange={(e) => upd('overdueMaxPerRun')(Number(e.target.value) || 50)} className="h-9 w-24" />
        </div>
        <p className="text-xs text-slate-500 max-w-sm">Left empty, it is set to the day the rule first runs — so turning it on never mails your older, imported invoices.</p>
      </div>
      <div className="space-y-3">
        {steps.map((s, i) => (
          <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-slate-700">Step {i + 1}: when</span>
              <Input type="number" min="1" value={s.days} onChange={(e) => setStep(i, { days: Number(e.target.value) || 1 })} className="h-8 w-20" />
              <span className="text-sm text-slate-700">days past due</span>
              <Button variant="ghost" size="sm" className="ml-auto h-8 text-slate-500" disabled={steps.length <= 1} onClick={() => upd('overdueSteps')(steps.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
            </div>
            <Input value={s.subject || ''} onChange={(e) => setStep(i, { subject: e.target.value })} placeholder="Email subject" className="h-9" />
            <Textarea value={s.template || ''} onChange={(e) => setStep(i, { template: e.target.value })} rows={3} />
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="outline" className="gap-1.5" onClick={() => upd('overdueSteps')([...steps, { days: (Number(steps[steps.length - 1]?.days) || 0) + 15, subject: 'Invoice past due', template: 'Hi {customer}, invoice #{number} for ${amount} is {days} days past due. Pay here: {payUrl}' }])}>
          <Plus className="w-4 h-4" />Add step
        </Button>
        <div className="flex flex-wrap gap-1.5">{VARS.map((v) => <span key={v} className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-xs font-mono">{v}</span>)}</div>
        <Button onClick={onSave} disabled={saving} className="bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid="save-overdue-sequence">{saving ? 'Saving…' : 'Save sequence'}</Button>
      </div>
    </Card>
  );
}
